import { Injectable } from '@angular/core';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
	key: string;
	direction: SortDirection;
}

@Injectable({
	providedIn: 'root',
})
export class SortingService {
	private getNestedValue(obj: any, key: string): any {
		return key.split('.').reduce((o, k) => (o && o[k] !== 'undefined' ? o[k] : null), obj);
	}

	sort<T>(array: readonly T[], sortState: SortState): T[] {
		// Guard against null, undefined, or non-iterable values
		if (!array || !Array.isArray(array)) {
			return [];
		}
		const result = [...array].sort((a, b) => this.compareValues(a, b, sortState.key, sortState.direction));
		return result;
	}

	private compareValues(a: any, b: any, key: string, direction: SortDirection): number {
		const valueA = this.getNestedValue(a, key);
		const valueB = this.getNestedValue(b, key);

		//Handle specifically currentChallenge sorting in NoSbCC, we should change the backend to deliver one number to represent this for sorting instead. Still to decide on how to deliver that.
		if (key.toLocaleLowerCase() == 'currentchallenge') {
			// Handle complete (challenge === 0) - assign a very high value so it sorts after all other challenges
			const getChallengeValue = (value: any): number => {
				if (value == null) return 0;
				// If challenge is 0, it means complete - give it a high value (higher than any phase 3 challenge)
				// Phase 3 max would be: 1000 * 3 + 75 = 3075, so we use 10000 for complete
				if (value.challenge === 0) {
					return 10000; // Complete sorts after all other challenges
				}
				return 1000 * (value.phase || 0) + (value.challenge || 0);
			};

			const challengeA = getChallengeValue(valueA);
			const challengeB = getChallengeValue(valueB);

			const comparison = challengeA - challengeB;
			if (comparison < 0) return direction === 'asc' ? -1 : 1;
			if (comparison > 0) return direction === 'asc' ? 1 : -1;
			if (comparison == 0) return 0;
		}

		// Handle null/undefined values based on sort directions
		if (valueA === null || valueA === undefined) {
			if (valueB === null || valueB === undefined) return 0;
			return direction === 'asc' ? -1 : 1;
		}

		if (valueB === null || valueB === undefined) {
			return direction === 'asc' ? 1 : -1;
		}

		// Handle dates
		if (valueA instanceof Date && valueB instanceof Date) {
			return direction === 'asc' ? valueA.getTime() - valueB.getTime() : valueB.getTime() - valueA.getTime();
		}

		// Handle strings
		if (typeof valueA === 'string' && typeof valueB === 'string') {
			return direction === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
		}

		// Handle numbers and other types
		const comparison = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
		return direction === 'asc' ? comparison : -comparison;
	}

	updateSortState(currentState: SortState, newKey: string, forceDirection?: SortDirection): SortState {
		if (forceDirection) {
			return { key: newKey, direction: forceDirection };
		}

		if (currentState.key === newKey) {
			return {
				key: newKey,
				direction: currentState.direction === 'asc' ? 'desc' : 'asc',
			};
		}

		return { key: newKey, direction: 'asc' };
	}
}
